import { useQuery } from "@apollo/client";
import { getRouteApi } from "@tanstack/react-router";
import { supplyChainPartnerLocationsQuery, getLocationOptions } from "../BookingForm";
import { type BookingStatus } from "../BookingStatusBadge";
import { Combobox } from "@/components/Combobox";
import { DateRangePicker } from "@/components/DateRangePicker";
import { FilterSheet } from "@/components/FilterSheet";
import { Table } from "@/components/Table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useFilterSheet } from "@/hooks/useFilterSheet";
import { toIsoDate, toJsDate } from "@/lib/utils/dates";
import { type BookingFilters } from "@/routes/(authed)/_layout/bookings.route";

const routeApi = getRouteApi("/(authed)/_layout/bookings");

/**
 * `dotClassName` pairs each status with its dot colour on the filter pills.
 * The `ring-*` twin is what the inactive (hollow) dot falls back to once the
 * fill is dropped, so both have to be written out in full for Tailwind to see
 * them.
 */
export const statusOptions = [
  { label: "Planned", value: "PLANNED", dotClassName: "bg-booking-status-planned ring-booking-status-planned" },
  { label: "Draft", value: "DRAFT", dotClassName: "bg-booking-status-draft ring-booking-status-draft" },
  { label: "Booked", value: "BOOKED", dotClassName: "bg-booking-status-booked ring-booking-status-booked" },
  { label: "Shipped", value: "SHIPPED", dotClassName: "bg-booking-status-shipped ring-booking-status-shipped" },
  {
    label: "Awaiting Payment",
    value: "AWAITING_PAYMENT",
    dotClassName: "bg-booking-status-awaiting-payment ring-booking-status-awaiting-payment",
  },
  { label: "Paid", value: "PAID", dotClassName: "bg-booking-status-paid ring-booking-status-paid" },
  { label: "Cancelled", value: "CANCELLED", dotClassName: "bg-booking-status-cancelled ring-booking-status-cancelled" },
  { label: "Archived", value: "ARCHIVED", dotClassName: "bg-booking-status-archived ring-booking-status-archived" },
  { label: "Closed", value: "CLOSED", dotClassName: "bg-booking-status-closed ring-booking-status-closed" },
] as const satisfies Array<{ label: string; value: BookingStatus; dotClassName: string }>;

export interface BookingsFiltersProps {
  filters: BookingFilters;
  setFilters: (filters: BookingFilters) => void;
  setInactiveFilters: React.Dispatch<React.SetStateAction<BookingFilters>>;
  usesLotNumber: boolean;
  usesAccountNumber: boolean;
}

export function BookingsFilters({
  filters,
  setFilters,
  setInactiveFilters,
  usesLotNumber,
  usesAccountNumber,
}: BookingsFiltersProps) {
  const navigate = routeApi.useNavigate();
  const { activeBusinessUnitId, activeBusinessUnitCountryCode } = useActiveBusinessUnit();

  // Fetch supply chain partners for source/destination options
  const { data: supplyChainPartnerData } = useQuery(supplyChainPartnerLocationsQuery, {
    variables: {
      businessUnit: activeBusinessUnitId || "",
      status: ["ACTIVE"],
      sortBy: [{ field: "BUSINESS_NAME", desc: false }],
    },
    skip: !activeBusinessUnitId,
  });

  const partnerOptions = getLocationOptions({
    edges: supplyChainPartnerData?.supplyChainPartners?.edges || [],
    businessUnitCountryCode: activeBusinessUnitCountryCode,
  });

  const { filterButton, filterSheetProps, localFilters, setLocalFilters } = useFilterSheet({
    initialFilters: filters,
    onApply: (newFilters) => {
      setFilters(newFilters);
      // The sheet is the authoritative statement of the filter set, so applying
      // or clearing it discards any inactive values left over from the pills.
      setInactiveFilters({});
      void navigate({
        search: (prev) => ({
          ...prev,
          name: newFilters.name && newFilters.name.trim() ? newFilters.name : undefined,
          lotNumber: newFilters.lotNumber && newFilters.lotNumber.trim() ? newFilters.lotNumber : undefined,
          accountNumber:
            newFilters.accountNumber && newFilters.accountNumber.trim() ? newFilters.accountNumber : undefined,
          // Explicitly empty rather than dropped, so beforeLoad can tell
          // "cleared on purpose" from "first visit" and leaves the defaults alone.
          status: newFilters.status ?? [],
          dateOpenedAfter: newFilters.dateOpenedAfter ? newFilters.dateOpenedAfter : undefined,
          dateOpenedBefore: newFilters.dateOpenedBefore ? newFilters.dateOpenedBefore : undefined,
          sourceIds: newFilters.sourceIds && newFilters.sourceIds.length > 0 ? newFilters.sourceIds : undefined,
          destinationIds:
            newFilters.destinationIds && newFilters.destinationIds.length > 0 ? newFilters.destinationIds : undefined,
        }),
      });
    },
    getActiveCount: (f) =>
      (f.status?.length || 0) +
      (f.dateOpenedAfter || f.dateOpenedBefore ? 1 : 0) +
      (f.sourceIds?.length || 0) +
      (f.destinationIds?.length || 0),
  });

  return (
    <>
      <Table.Filters {...filterButton} />

      <FilterSheet {...filterSheetProps}>
        <div className="flex flex-col gap-2">
          <Label htmlFor="name-filter">Booking Name</Label>
          <Input
            id="name-filter"
            type="text"
            placeholder="Search by booking name"
            value={localFilters.name || ""}
            onChange={(e) => {
              setLocalFilters({ ...localFilters, name: e.target.value });
            }}
          />
          {usesLotNumber ? (
            <>
              <Label htmlFor="lot-number-filter">Lot Number</Label>
              <Input
                id="lot-number-filter"
                type="text"
                placeholder="Search by lot number"
                value={localFilters.lotNumber || ""}
                onChange={(e) => {
                  setLocalFilters({ ...localFilters, lotNumber: e.target.value });
                }}
              />
            </>
          ) : null}
          {usesAccountNumber ? (
            <>
              <Label htmlFor="account-number-filter">Contract Number</Label>
              <Input
                id="account-number-filter"
                type="text"
                placeholder="Search by contract number"
                value={localFilters.accountNumber || ""}
                onChange={(e) => {
                  setLocalFilters({ ...localFilters, accountNumber: e.target.value });
                }}
              />
            </>
          ) : null}
          <Label htmlFor="source-filter">Source</Label>
          <Combobox
            id="source-filter"
            type="multi"
            options={partnerOptions}
            selected={localFilters.sourceIds || []}
            onChange={(sourceIds) => {
              setLocalFilters({ ...localFilters, sourceIds });
            }}
            placeholder="Select sources"
          />
          <Label htmlFor="destination-filter">Destination</Label>
          <Combobox
            id="destination-filter"
            type="multi"
            options={partnerOptions}
            selected={localFilters.destinationIds || []}
            onChange={(destinationIds) => {
              setLocalFilters({ ...localFilters, destinationIds });
            }}
            placeholder="Select destinations"
          />
          <Label htmlFor="status-filter">Status</Label>
          <Combobox
            id="status-filter"
            type="multi"
            options={statusOptions}
            selected={localFilters.status || []}
            onChange={(status) => {
              setLocalFilters({ ...localFilters, status });
            }}
            placeholder="Select status"
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="date-range-filter">Planned Shipping Date</Label>
          <DateRangePicker
            id="date-range-filter"
            dateRange={{
              from: toJsDate(localFilters.dateOpenedAfter),
              to: toJsDate(localFilters.dateOpenedBefore),
            }}
            onChange={(range) => {
              setLocalFilters({
                ...localFilters,
                dateOpenedAfter: range?.from ? toIsoDate(range.from) : undefined,
                dateOpenedBefore: range?.to ? toIsoDate(range.to) : undefined,
              });
            }}
          />
        </div>
      </FilterSheet>
    </>
  );
}
